import React, { useState } from 'react'
import styled from 'styled-components'

// Components
import Text from './Text'
import { HoverText, IPointerPosition } from './tag/JobTag'

// Styles
import { hsla } from '../styles/colors'

// Types
import { AnyColor } from '../common/models/colors'

interface ILabel {
  label: string
  hoverLabel?: string
}

interface IProps {
  className?: string
  total: number
  complete: number
  color?: AnyColor
  labels?: ILabel[]
  // Segment height and the gap between segments (px). Defaults reproduce the
  // original 8px segment / 2px gap look so existing callers stay unchanged.
  height?: number
  gap?: number
  // Raw CSS colors (e.g. a culture value color) that override the themed
  // `color` for the filled / unfilled segments.
  barColor?: string
  trackColor?: string
}

const ValueBar: React.FC<IProps> = ({
  className,
  total,
  complete,
  color,
  labels,
  height = 8,
  gap = 2,
  barColor,
  trackColor,
}) => {
  const [pointerPosition, setPointerPosition] = useState<IPointerPosition>({})
  const [hoverIndex, setHoverIndex] = useState<number>(-1)

  const handleMouseEnter = (e: React.MouseEvent, index: number) => {
    if (!labels || labels.length === 0 || !labels[index].hoverLabel) return
    setHoverIndex(index)
    setPointerPosition({
      x: Number(e.pageX.toFixed(0)),
      y: Number(e.pageY.toFixed(0)),
    })
  }

  const handleMouseLeave = (index: number) => {
    if (!labels || labels.length === 0 || !labels[index].hoverLabel) return
    setHoverIndex(-1)
    setPointerPosition({})
  }

  const parts: string[] = []

  for (let i = 0; i < total; i++) {
    parts.push('')
  }

  return (
    <Container className={className} gap={gap}>
      {parts.map((_, index) => (
        <Part
          key={`value-bar-${index}`}
          color={color || ('primary' as AnyColor)}
          complete={index + 1 <= complete}
          hoverable={
            labels &&
            labels.length > 0 &&
            labels[index].hoverLabel !== undefined
          }
          height={height}
          barColor={barColor}
          trackColor={trackColor}
          onPointerEnter={(e) => handleMouseEnter(e, index)}
          onPointerLeave={() => handleMouseLeave(index)}
        >
          {labels && labels.length > 0 && (
            <Label textStyle="labelHeavy" tag="span" color="textLight">
              {labels[index].label}
            </Label>
          )}
          {labels &&
            labels.length > 0 &&
            labels[index].hoverLabel &&
            hoverIndex === index &&
            pointerPosition.x &&
            pointerPosition.y && (
              <HoverText
                text={labels[index].hoverLabel || ''}
                position={pointerPosition}
              />
            )}
        </Part>
      ))}
    </Container>
  )
}

export default ValueBar

const Container = styled.div<{ gap: number }>`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${({ gap }) => gap}px;
`

const Part = styled.div<{
  height: number
  complete: boolean
  color: AnyColor
  hoverable?: boolean
  barColor?: string
  trackColor?: string
}>`
  flex: 1;
  min-width: 0;
  background-color: ${({ complete, color, theme, barColor, trackColor }) =>
    complete
      ? barColor ?? theme.colors[color]
      : trackColor ?? hsla(theme.colors[color], 0.5)};
  height: ${({ height }) => height}px;
  position: relative;
  cursor: ${({ hoverable }) => hoverable && 'pointer'};

  &:first-of-type {
    border-radius: 24px 0px 0px 24px;
  }

  &:last-of-type {
    border-radius: 0px 24px 24px 0px;
  }
`

const Label = styled(Text)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`
